class AdminService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'https://oauth.remote-assistant.ai';
  }

  async makeAuthenticatedRequest(endpoint, options = {}) {
    try {
      // Get token from current user session
      const { CognitoUserPool } = require('amazon-cognito-identity-js');
      
      const poolData = {
        UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_eQ4oSjtGM',
        ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '7123hmqco0fqs1j2hfd0kau4hs'
      };
      
      const userPool = new CognitoUserPool(poolData);
      const currentUser = userPool.getCurrentUser();
      
      if (!currentUser) {
        throw new Error('No authenticated user');
      }
      
      const token = await new Promise((resolve, reject) => {
        currentUser.getSession((err, session) => {
          if (err) {
            reject(err);
            return;
          }
          
          if (session.isValid()) {
            resolve(session.getIdToken().getJwtToken());
          } else {
            reject(new Error('Session invalid'));
          }
        });
      });
      
      // Try the OAuth endpoint first (JWT authentication)
      try {
        const response = await fetch(`${this.baseURL}${endpoint}`, {
          ...options,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers
          }
        });

        if (response.ok) {
          return await response.json();
        }
        
        // If we get a 403 or authentication error, the endpoint might not be properly configured
        if (response.status === 403 || response.status === 401) {
          const errorData = await response.json().catch(() => ({}));
          
          // Check if this is the HMAC authentication error
          if (errorData.message && errorData.message.includes('Invalid key=value pair')) {
            throw new Error('Admin endpoint authentication not properly configured. Please contact support.');
          }
        }
        
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
        
      } catch (fetchError) {
        // If it's a network error or the specific HMAC error, provide helpful message
        if (fetchError.message.includes('Invalid key=value pair') || 
            fetchError.message.includes('authentication not properly configured')) {
          throw new Error('Admin portal authentication is not properly configured. The API Gateway needs to be updated to use Cognito User Pool authorization for admin endpoints.');
        }
        throw fetchError;
      }
      
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async getWaitlist() {
    return await this.makeAuthenticatedRequest('/admin/waitlist');
  }

  async approveWaitlistUser(email) {
    return await this.makeAuthenticatedRequest(`/admin/waitlist/${encodeURIComponent(email)}/approve`, {
      method: 'POST'
    });
  }

  async getBusinessTypes() {
    return await this.makeAuthenticatedRequest('/admin/templates/business-types');
  }

  async getTemplates() {
    return await this.makeAuthenticatedRequest('/admin/templates');
  }

  async getBusinessConfigs() {
    return await this.makeAuthenticatedRequest('/admin/business-configs');
  }

  async setTemplate(businessType, businessId) {
    return await this.makeAuthenticatedRequest('/admin/templates', {
      method: 'POST',
      body: JSON.stringify({ business_type: businessType, business_id: businessId })
    });
  }

  async deleteTemplate(businessType) {
    return await this.makeAuthenticatedRequest(`/admin/templates/${encodeURIComponent(businessType)}`, {
      method: 'DELETE'
    });
  }

  // ── Cognito user management ──────────────────────────────────────────────

  async listCognitoUsers(search = '', status = '') {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return await this.makeAuthenticatedRequest(`/admin/cognito/users${qs}`);
  }

  async getCognitoUser(username) {
    return await this.makeAuthenticatedRequest(`/admin/cognito/users/${encodeURIComponent(username)}`);
  }

  async disableCognitoUser(username) {
    return await this.makeAuthenticatedRequest(`/admin/cognito/users/${encodeURIComponent(username)}/disable`, {
      method: 'POST'
    });
  }

  async enableCognitoUser(username) {
    return await this.makeAuthenticatedRequest(`/admin/cognito/users/${encodeURIComponent(username)}/enable`, {
      method: 'POST'
    });
  }

  async deleteCognitoUser(username) {
    return await this.makeAuthenticatedRequest(`/admin/cognito/users/${encodeURIComponent(username)}`, {
      method: 'DELETE'
    });
  }

  async updateCognitoUserAttributes(username, attributes) {
    return await this.makeAuthenticatedRequest(`/admin/cognito/users/${encodeURIComponent(username)}/attributes`, {
      method: 'PUT',
      body: JSON.stringify({ attributes })
    });
  }

  async lookupBusinessByPhone(phone) {
    return await this.makeAuthenticatedRequest(`/admin/business/lookup?phone=${encodeURIComponent(phone)}`);
  }

  async getLedger(businessId) {
    return await this.makeAuthenticatedRequest(`/admin/ledger/${encodeURIComponent(businessId)}`);
  }

  async recalculateLedger(businessId, dryRun = true) {
    return await this.makeAuthenticatedRequest(`/admin/ledger/${encodeURIComponent(businessId)}/recalculate`, {
      method: 'POST',
      body: JSON.stringify({ dry_run: dryRun })
    });
  }

  async rebuildLedger(businessId, dryRun = true) {
    return await this.makeAuthenticatedRequest(`/admin/ledger/${encodeURIComponent(businessId)}/rebuild`, {
      method: 'POST',
      body: JSON.stringify({ dry_run: dryRun })
    });
  }

  // --- AI Ops ---
  async getAIOpsStatus() {
    return await this.makeAuthenticatedRequest('/admin/aiops/status');
  }

  async pauseTriage() {
    return await this.makeAuthenticatedRequest('/admin/aiops/triage/pause', { method: 'POST' });
  }

  async resumeTriage() {
    return await this.makeAuthenticatedRequest('/admin/aiops/triage/resume', { method: 'POST' });
  }

  async getTriageResults({ phone = '', days = 7, scenario = '' } = {}) {
    const params = new URLSearchParams();
    if (phone) params.set('phone', phone);
    if (days) params.set('days', days);
    if (scenario) params.set('scenario', scenario);
    return await this.makeAuthenticatedRequest(`/admin/aiops/triage/results?${params}`);
  }

  async getInvestigations() {
    return await this.makeAuthenticatedRequest('/admin/aiops/investigations');
  }

  async getInvestigationLog(ticket, attempt) {
    return await this.makeAuthenticatedRequest(`/admin/aiops/investigations/${encodeURIComponent(ticket)}/${attempt}/log`);
  }

  async getAIOpsBilling() {
    return await this.makeAuthenticatedRequest('/admin/aiops/billing');
  }

  async closeTickets(tickets, resolution = "Won't Fix") {
    return await this.makeAuthenticatedRequest('/admin/aiops/tickets/close', {
      method: 'POST',
      body: JSON.stringify({ tickets, resolution })
    });
  }
}

export const adminService = new AdminService();